<script setup lang="ts">
import type { NuxtError } from '#app'

/**
 * Fehlerseite (404 / 5xx) im Vokabular der Landing: ruhig, ein Ausweg zurück
 * zur Startseite. Nicht indexieren — die Landing ist die einzige echte URL.
 */
const props = defineProps<{ error: NuxtError }>()

const notFound = computed(() => props.error?.statusCode === 404)
const code = computed(() => props.error?.statusCode ?? 500)

useSeoMeta({
  title: notFound.value ? 'Page not found | Clarify' : 'Something went wrong | Clarify',
  robots: 'noindex, nofollow'
})

const backHome = () => clearError({ redirect: '/' })
</script>

<template>
  <UApp>
    <main class="err">
      <div class="err-card">
        <img src="/brand/clarify-mark.svg" alt="Clarify" width="44" height="44" class="err-logo">
        <p class="err-code">{{ code }}</p>
        <h1 class="err-title">
          {{ notFound ? 'This page does not exist.' : 'Something went wrong.' }}
        </h1>
        <p class="err-text">
          <template v-if="notFound">
            The link may be outdated — everything about Clarify lives on the start page.
          </template>
          <template v-else>
            Please try again in a moment. If it keeps happening,
            <a :href="`mailto:${CONTACT_EMAIL}`" class="err-link">let our support know</a>.
          </template>
        </p>
        <UButton label="Back to the start page" trailing-icon="i-lucide-arrow-right" class="err-cta" @click="backHome" />
      </div>
    </main>
  </UApp>
</template>

<style scoped>
.err {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  background: var(--nb-bg);
  color: var(--nb-ink);
}

.err-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  max-width: 460px;
  padding: 32px;
  background: var(--nb-surface);
  border: 1px solid var(--nb-hairline);
  box-shadow: var(--shadow-card);
}

.err-logo {
  margin-bottom: 8px;
}

.err-code {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  color: var(--nb-ink-faint);
}

.err-title {
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.err-text {
  color: var(--nb-ink-muted);
  line-height: 1.6;
  margin-bottom: 8px;
}

.err-link {
  color: var(--nb-ink);
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* Wie die übrigen Primär-CTAs: weiße Schrift auf Brand (6.6:1), Brand-Hover */
.err-cta {
  background: var(--nb-accent);
  color: #fff;
  transition: background var(--dur) var(--ease-out);
}

.err-cta:hover {
  background: var(--nb-accent-hover);
}
</style>
